import { useAui, useAuiState, useThreadViewportStore } from '@assistant-ui/react';
import { useChatRuntime } from 'components/Chat/AssistantChatRuntime';
import useChatThreadScroll from 'hooks/chat/useChatThreadScroll';
import { useTranslation } from 'react-i18next';
import { hasAssistantStreamingOutput } from 'utils/chat/chatHelpers';

/**
 * Presentation view model for the complete chat panel.
 *
 * Use from `ChatPanelRuntimeContent` to compose assistant-ui state, runtime errors,
 * suggestion controls, composer state, loading placeholders, and scroll behavior into
 * `ChatPanelView` props. Data loading and websocket request execution live in lower hooks.
 *
 * @param {Object} props
 * @param {boolean} props.hasOlderMessages Whether older pages can be loaded.
 * @param {React.ReactNode} [props.headerEndContent] Optional trailing controls rendered in the panel header.
 * @param {React.ReactNode} [props.headerStartContent] Optional leading control rendered in the panel header.
 * @param {boolean} props.hideBackButton Whether the header back button should be hidden.
 * @param {boolean} props.hideComposer Whether the composer should be hidden.
 * @param {boolean} props.isConnected Whether websocket-backed send controls are available.
 * @param {boolean} props.isLoadingOlderMessages Whether pagination is currently loading.
 * @param {Function} [props.onBack] Header back action.
 * @param {Function} [props.onLoadOlderMessages] Loads the next older message page.
 * @param {Function} [props.onSuggestionSelected] Called before a prompt suggestion is sent.
 * @param {boolean} props.shouldScrollThreadToBottom Whether the current thread should be bottomed after open/switch.
 * @param {string} [props.threadViewKey] Current thread identity used for thread-switch scroll behavior.
 * @param {boolean} [props.showSuggestions=false] Whether prompt suggestions should be shown.
 * @param {Array<Object>} [props.suggestions=[]] Prompt suggestion models.
 * @param {string} [props.suggestionsTitle] Optional heading shown above prompt suggestions.
 * @param {string} [props.suggestionsVariant] Optional suggestion presentation variant.
 * @param {Object} props.viewModel Header/view copy for this chat panel.
 * @returns {Object} Props grouped for `ChatPanelView`.
 */
const useChatPanelViewModel = ({
    hasOlderMessages,
    headerEndContent,
    headerStartContent,
    hideBackButton,
    hideComposer,
    isConnected,
    isLoadingOlderMessages,
    onBack,
    onLoadOlderMessages,
    onSuggestionSelected,
    shouldScrollThreadToBottom,
    threadViewKey,
    showSuggestions = false,
    suggestions = [],
    suggestionsTitle,
    suggestionsVariant,
    viewModel,
}) => {
    const { t } = useTranslation('chat');
    const aui = useAui();
    const threadViewportStore = useThreadViewportStore();
    const composerText = useAuiState((state) => state.composer.text);
    const messages = useAuiState((state) => state.thread.messages);
    const isRunning = useAuiState((state) => state.thread.isRunning);
    const { chatError, clearChatError, isRetrying, manualAssistantDraft, retryLastRequest } = useChatRuntime();
    const headerTitle = viewModel?.header?.title;
    const headerIcon = viewModel?.header?.icon;
    const headerInfoTooltip = viewModel?.header?.infoTooltip;
    const composerPlaceholder = viewModel?.composer?.placeholder;
    const emptyState = viewModel?.emptyState;
    const loadingActivity = viewModel?.loadingActivity;
    const hasStreamingOutput = hasAssistantStreamingOutput({ isRunning, messages });
    const hasBlockingChatError = Boolean(chatError?.actionType);
    const {
        onThreadKeyDown,
        onThreadScroll,
        onThreadTouchMove,
        onThreadWheel,
        showTopScrollGradient,
        threadViewportRef,
    } = useChatThreadScroll({
        hasOlderMessages,
        isLoadingOlderMessages,
        isRetrying,
        isRunning,
        manualAssistantDraft,
        messages,
        onLoadOlderMessages,
        shouldScrollThreadToBottom,
        showSuggestions,
        suggestionsLength: suggestions.length,
        threadViewKey,
    });

    const scrollThreadToBottom = () => {
        threadViewportStore.getState().scrollToBottom({ behavior: 'smooth' });
    };

    const sendComposerMessage = () => {
        if (isRunning || isRetrying || !isConnected) return;

        clearChatError();
        scrollThreadToBottom();
        aui.composer().send();
    };

    const sendSuggestion = (text) => {
        onSuggestionSelected?.();
        aui.composer().setText(text);
        sendComposerMessage();
    };

    return {
        composer: {
            isVisible: !hideComposer,
            props: {
                disabled: hasBlockingChatError || isRunning || isRetrying || !isConnected,
                disabledTooltip: isRunning
                    ? t('submission.ai-responding', 'Please wait while the AI is responding.')
                    : '',
                placeholder:
                    composerPlaceholder || t('submission.ask-a-question', 'Ask a question about this feedback'),
                value: composerText,
                onChange: (value) => aui.composer().setText(value),
                onSend: sendComposerMessage,
            },
        },
        error: {
            action: chatError?.actionType
                ? {
                      icon: chatError.icon,
                      label: chatError.actionLabel,
                  }
                : null,
            message: chatError?.message || '',
            onAction: () => {
                clearChatError();
                if (chatError?.actionType === 'refresh') {
                    window.location.reload();
                    return;
                }

                retryLastRequest?.();
            },
        },
        header: {
            endContent: headerEndContent,
            icon: headerIcon,
            infoTooltip: headerInfoTooltip,
            isTitleCentered: !hideBackButton,
            onBack,
            showBackButton: !hideBackButton,
            startContent: headerStartContent,
            title: headerTitle,
        },
        suggestions: {
            disabled: hasBlockingChatError || isRunning || isRetrying || !isConnected,
            isVisible: showSuggestions && suggestions.length > 0,
            items: suggestions,
            title: suggestionsTitle,
            variant: suggestionsVariant,
            onSelect: sendSuggestion,
        },
        thread: {
            emptyState: {
                body: emptyState?.body || '',
                firstName: emptyState?.firstName || '',
                isVisible: Boolean(emptyState?.isVisible) && messages.length === 0,
            },
            isLoadingOlderMessages,
            messagesProps: {
                hasLoadingPlaceholder: (isRunning && !hasStreamingOutput) || (isRetrying && !manualAssistantDraft),
                isRunning,
                loadingActivity,
                messages,
            },
            onKeyDown: onThreadKeyDown,
            onScroll: onThreadScroll,
            onTouchMove: onThreadTouchMove,
            onWheel: onThreadWheel,
            showTopScrollGradient,
            viewportProps: {
                ref: threadViewportRef,
                autoScroll: !isRunning && !manualAssistantDraft,
                scrollToBottomOnRunStart: false,
                scrollToBottomOnThreadSwitch: false,
            },
        },
    };
};

export default useChatPanelViewModel;
